import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { expressMutation, getQueryKey, useExpressQuery } from '../../../../repository/react-query/useExpress';
import { useContext } from 'react';
import { NotificationContext } from '../../../../NotificationContext';

const baseQueryKey = 'silo';

/**
 * Fetch all silos
 */
export const useGetSilos = ({ queryParams = {}, enabled = true } = {}) => {
    return useQuery({
        queryKey: [baseQueryKey, getQueryKey(queryParams)],
        queryFn: useExpressQuery({ endpoint: '/silo', params: queryParams }),
        enabled,
    });
};

/*
 * Create a silo record
 *
 * @example
 * const { mutate, isLoading } = useCreateSilo();
 *
 * // Create a silo
 * mutate({
 *   silo_number: "12345",
 *   processor_id: "123456",
 *   capacity: 4
 * });
 */
export const useCreateSilo = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: ({ silo_number, processor_id, capacity }) =>
            expressMutation({
                method: 'post',
                endpoint: '/silo',
                body: {
                    silo_number,
                    processor_id,
                    capacity,
                },
            }),
        onError: () => {
            showSnackbar({ severity: 'error', message: 'Failed to create silo' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
        },
    });
};

/**
 * Updates a silo record
 *
 * @example
 * const { mutate, isLoading } = useMutateSilo();
 *
 * // Update a silo
 * mutate({
 *   _id: "12345",
 *   capacity: 5,
 * });
 */
export const useMutateSilo = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: ({ _id, silo_number, processor_id, capacity }) =>
            expressMutation({
                method: 'patch',
                endpoint: `/silo/${_id}`,
                body: {
                    silo_number,
                    processor_id,
                    capacity,
                },
            }),
        onMutate: async (newItem) => {
            // Cancel any ongoing refetches to avoid overwriting optimistic update
            await queryClient.cancelQueries({ queryKey: [baseQueryKey] });
            const queries = queryClient.getQueryCache().findAll({ queryKey: [baseQueryKey] });

            queries.forEach((query) => {
                const previousData = query.state.data;

                if (Array.isArray(previousData)) {
                    queryClient.setQueryData(
                        query.queryKey,
                        previousData.map((item) => (item._id === newItem._id ? newItem : item))
                    );
                } else if (previousData && typeof previousData === 'object') {
                    queryClient.setQueryData(query.queryKey, newItem);
                }
            });

            return { queries };
        },
        onError: (err, _, ctx) => {
            // Rollback to previous data
            if (ctx?.queries) {
                ctx.queries.forEach((query) => {
                    queryClient.setQueryData(query.queryKey, query.state.data);
                });
            }

            // Show error
            showSnackbar({ severity: 'error', message: 'An error occurred while updating the silo. Please try again or contact support if the problem persists.' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
        },
    });
};

/**
 * Soft deletes silo records.
 *
 * @example
 * const { mutate, isLoading } = useDeleteSilo();
 *
 * // Delete a silo
 * mutate({
 *   _id
 * });
 */
export const useDeleteSilo = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: ({ _id }) =>
            expressMutation({
                method: 'delete',
                endpoint: `/silo/${_id}`,
            }),
        onError: (err) => {
            const res = err?.response?.data;
            showSnackbar({ severity: 'error', message: res?.message ?? 'Failed to delete silo' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
        },
    });
};
